/* eslint-disable react/no-danger */
import React, { Context, memo, ReactElement, useContext, useEffect, useState } from 'react';
import { exists, t } from 'i18next';
import { Link } from 'react-router-dom';
import { Market } from '@oneaudi/avp-models';
import styled from 'styled-components';
import { HorizontalSpacing, TypoScale, VerticalSpacing } from '../../common/spacings';
import { screen } from '../../common/breakpoints';
import { getCupraLegalNoticeWebsite } from '../../i18n/websites';
import { ApiClientContext } from '../../services/ApiClientService';
import { CupraApiClient } from '../../client/CupraApiClient';

const StyledFooterContainer = styled.div`
  background-color: #1b1b1b;
  color: #ffffff;
  font-weight: 400;
  line-height: 24px;
  padding: 3rem;

  @media screen and ${screen.s} {
    align-items: initial;
    padding-left: ${HorizontalSpacing.S};
    padding-right: ${HorizontalSpacing.S};
    padding-top: ${VerticalSpacing.S};
    padding-bottom: ${VerticalSpacing.S};
    font-size: ${TypoScale.S};
  }

  @media screen and ${screen.m} {
    padding-left: ${HorizontalSpacing.M};
    padding-right: ${HorizontalSpacing.M};
    padding-top: ${VerticalSpacing.M};
    padding-bottom: ${VerticalSpacing.M};
    flex-direction: row;
    font-size: ${TypoScale.S};
  }

  @media screen and ${screen.l} {
    align-items: initial;
    padding-left: ${HorizontalSpacing.L};
    padding-right: ${HorizontalSpacing.L};
    padding-top: ${VerticalSpacing.L};
    padding-bottom: ${VerticalSpacing.L};
    font-size: ${TypoScale.M};
  }

  @media screen and ${screen.xl} {
    align-items: initial;
    padding-left: ${HorizontalSpacing.XL};
    padding-right: ${HorizontalSpacing.XL};
    padding-top: ${VerticalSpacing.XL};
    padding-bottom: ${VerticalSpacing.XL};
    font-size: ${TypoScale.L};
  }
`;

const StyledNavigationContainer = styled.div`
  display: flex;
  margin-bottom: 3rem;

  span {
    padding: 0 1rem 0 0;
  }

  a {
    color: white;
    font-weight: 500;
    text-decoration: none;
    padding: 0 1rem 0 1rem;
    font-size: small;

    @media screen and ${screen.m} {
      font-weight: 700;
      font-size: ${TypoScale.L};
    }
  }

  a:hover {
    color: #999;
    cursor: pointer;
    text-decoration: underline;
  }
`;

const StyledLink = styled(Link)`
  padding: 0 1rem 0 1rem;
  font-weight: 400;
  font-size: small;

  @media screen and ${screen.m} {
    font-weight: 700;
    font-size: ${TypoScale.L};
  }
`;

const StyledConsumptionNotice = styled.div`
  p:nth-child(-n + 2) {
    margin-bottom: 1rem;
  }
  a {
    color: #999;
    font-weight: 500;
    text-decoration: none;
  }
  a:hover {
    color: white;
    text-decoration: none;
  }
`;

const thirdPartySoftwareLink = (locale: string, market: Market, disabled?: boolean) => {
  if (disabled) {
    return '';
  }

  return (
    <StyledLink
      to={`/third-party-software?locale=${locale}&market=${market}`}
      target="_blank"
      rel="noopener noreferrer"
      role="third-party-link"
    >
      {t('footer.thirdPartySoftware')}
    </StyledLink>
  );
};

interface FooterProps {
  locale: string;
  market: Market;
  disableThirdPartyLink?: boolean;
  onError?: React.Dispatch<React.SetStateAction<number>>;
}

export const Footer = memo((props: FooterProps): ReactElement => {
  const { locale, market, disableThirdPartyLink, onError } = props;
  const hckClient = useContext<CupraApiClient>(ApiClientContext as Context<CupraApiClient>);

  const [wltpDisclaimer, setWltpDisclaimer] = useState<string>('');
  useEffect(() => {
    hckClient
      .getDisclaimerTexts(market)
      .then((disclaimerTexts) => disclaimerTexts.consumptionAndEmission)
      .then((wltpText) => setWltpDisclaimer(wltpText))
      .catch((_error) => {
        onError && onError(500);
      });
  }, [hckClient, market, onError]);

  return (
    <StyledFooterContainer>
      <StyledNavigationContainer data-testid="footer-navigation">
        <span>{t('footer.copyright')}</span>
        <a
          href={getCupraLegalNoticeWebsite(market)}
          referrerPolicy="no-referrer"
          rel="noopener noreferrer"
          target="_blank"
          data-testid="footer-imprint"
        >
          {t('footer.imprint')}
        </a>
        {thirdPartySoftwareLink(locale, market, disableThirdPartyLink)}
      </StyledNavigationContainer>
      <StyledConsumptionNotice
        data-testid="footer-wltpDisclaimer"
        dangerouslySetInnerHTML={{ __html: wltpDisclaimer }}
      />
      <div
        data-testid="footer-visNote"
        dangerouslySetInnerHTML={{
          __html: exists('footer.nonbindingVisualizationsNote')
            ? (t('footer.nonbindingVisualizationsNote') as string)
            : '',
        }}
      />
    </StyledFooterContainer>
  );
});
